import { useQuery, type UseQueryResult } from '@tanstack/react-query'
import { apiClient, authHeader } from '../lib/api'
import type { StudentProfile } from '@tnf-recruit/shared'
import { useAuth } from './useAuth'

export interface StudentSearchParams {
  query?: string
  /**
   * Recruiter-selected event to filter/sort by.
   * Matches students who list this event in their primaryEvents.
   */
  eventId?: string
  eventCutoff?: number
  graduationYear?: number
  minGPA?: number
  minSatScore?: number
  includeMissingSatScore?: boolean
  minActScore?: number
  includeMissingActScore?: boolean
  state?: string
  competitionTeam?: 'men' | 'women'
  genderIdentity?: string
  page?: number
  limit?: number
  sortBy?:
    | 'name'
    | 'graduationYear'
    | 'gpa'
    | 'satScore'
    | 'actScore'
    | 'personalRecord'
    | 'competitionTeam'
    | 'createdAt'
  sortOrder?: 'asc' | 'desc'
}

export interface StudentSearchResult {
  data: StudentProfile[]
  pagination: {
    page: number
    limit: number
    total: number
    totalPages: number
  }
}

export function useStudentSearch(
  params: StudentSearchParams,
  enabled = true
): UseQueryResult<StudentSearchResult, unknown> {
  const { getAccessToken } = useAuth()

  return useQuery<StudentSearchResult>({
    queryKey: ['studentSearch', params],
    queryFn: async (): Promise<StudentSearchResult> => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/students/search', {
        ...authHeader(token),
        params,
      })
      return {
        data: data.data ?? [],
        pagination: data.pagination ?? { page: 1, limit: 20, total: 0, totalPages: 0 },
      }
    },
    enabled,
  })
}

export interface StudentSearchIdsResult {
  ids: string[]
  total: number
  capped: boolean
}

/**
 * Fetch the ids of every student matching the current search filters (not
 * just the current page). Backs "select all N matching" on the browse page.
 * Lazy by default — call `refetch()` rather than relying on mount.
 */
export function useStudentSearchIds(
  params: StudentSearchParams
): UseQueryResult<StudentSearchIdsResult, unknown> {
  const { getAccessToken } = useAuth()

  return useQuery<StudentSearchIdsResult>({
    queryKey: ['studentSearchIds', params],
    queryFn: async (): Promise<StudentSearchIdsResult> => {
      const token = await getAccessToken()
      const { data } = await apiClient.get('/students/search/ids', {
        ...authHeader(token),
        params,
      })
      return {
        ids: data.ids ?? [],
        total: data.total ?? 0,
        capped: data.capped ?? false,
      }
    },
    enabled: false,
  })
}

/**
 * Fetch a single student profile by ID (recruiter view).
 */
export function useStudentDetail(
  studentId: string | undefined
): UseQueryResult<StudentProfile, unknown> {
  const { getAccessToken } = useAuth()

  return useQuery<StudentProfile>({
    queryKey: ['studentDetail', studentId],
    queryFn: async () => {
      const token = await getAccessToken()
      const { data } = await apiClient.get(`/students/${studentId}`, authHeader(token))
      return data.data as StudentProfile
    },
    enabled: !!studentId,
  })
}
